import { z } from 'zod';
import { STUDIO_DRAWER_SEARCH_PARAM } from '@/constants/search-params';
import { parsePositiveIntegerSearchValue } from '@/lib/route-search';

const optionalUrlStringSchema = z.string().url().or(z.literal(''));

const positiveIntegerFromUnknownSearchField = z.preprocess(
  parsePositiveIntegerSearchValue,
  z.number().int().positive().optional(),
);

export const homeSearchSchema = z.object({
  [STUDIO_DRAWER_SEARCH_PARAM]: positiveIntegerFromUnknownSearchField,
});

export const bootstrapUserSchema = z.object({
  id: z.number().int().positive(),
  login: z.string(),
  displayName: z.string(),
  roles: z.array(z.string()),
});

export const bootstrapDataSchema = z.object({
  apiBase: z.string().url(),
  siteUrl: z.string().url(),
  themeUrl: z.string().url(),
  nonce: z.string(),
  isLoggedIn: z.boolean(),
  user: bootstrapUserSchema.nullable(),
});

export const studioRouteImageSchema = z.object({
  id: z.number().int().positive(),
  url: z.string().url(),
  alt: z.string(),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
});

export const studioSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string(),
  title: z.string(),
  location: z.string(),
  kakaoMapUrl: optionalUrlStringSchema,
  naverMapUrl: optionalUrlStringSchema,
  spaceCloudUrl: optionalUrlStringSchema,
  adminContact: z.string(),
  clickCount: z.number().int().nonnegative(),
  likeCount: z.number().int().nonnegative(),
  routeImage: studioRouteImageSchema.nullable(),
  routeImages: z.array(studioRouteImageSchema).default([]),
  shareUrl: z.string().url(),
});

export const studiosResponseSchema = z.array(studioSchema);

export const studioClickResponseSchema = z.object({
  id: z.number().int().positive(),
  clickCount: z.number().int().nonnegative(),
});

export const studioLikeResponseSchema = z.object({
  id: z.number().int().positive(),
  liked: z.boolean(),
  likeCount: z.number().int().nonnegative(),
});

export const inquiryResponseSchema = z.object({
  id: z.number().int().positive(),
  status: z.literal('pending'),
  studioId: z.number().int().positive(),
  attachmentIds: z.array(z.number().int().positive()),
  message: z.string(),
});

export const inquiryFormSchema = z.object({
  studioId: z.number().int().positive(),
  content: z.string().trim().min(1),
  imageFiles: z.array(z.instanceof(File)).optional(),
});

export const studioAddRequestFormSchema = z.object({
  name: z.string().trim().min(1),
  address: z.string().trim().min(1),
  extraInfo: z.string().trim().optional(),
});

export const studioAddRequestResponseSchema = z.object({
  id: z.number().int().positive(),
  status: z.literal('pending'),
  name: z.string(),
  address: z.string(),
  message: z.string(),
});

export type HomeSearch = z.infer<typeof homeSearchSchema>;
export type InquiryFormValues = z.infer<typeof inquiryFormSchema>;
export type StudioAddRequestFormValues = z.infer<typeof studioAddRequestFormSchema>;
